import React, { useEffect, useState } from "react";
import { BellRing, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { disconnectPush, enablePush, inspectPush, pushError, pushUnavailableReason, testPush } from "@/lib/push";

export function PushSettings() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [message, setMessage] = useState("");
  const reason = pushUnavailableReason();
  const denied = "Notification" in window && Notification.permission === "denied";

  useEffect(() => {
    let mounted = true;
    if (reason) { setBusy(false); return; }
    inspectPush().then((state) => {
      if (mounted) { setEnabled(state.enabled); setConfigured(state.configured); }
    }).catch((e) => { if (mounted) setMessage(pushError(e)); })
      .finally(() => { if (mounted) setBusy(false); });
    return () => { mounted = false; };
  }, [user.id, reason]);

  const toggle = async () => {
    setBusy(true); setMessage("");
    try {
      if (enabled) await disconnectPush(); else await enablePush();
      setEnabled(!enabled);
      setMessage(enabled ? "Pushberichten zijn uitgeschakeld op dit apparaat." : "Pushberichten staan aan op dit apparaat, ook als de app gesloten is.");
    } catch (error) { setMessage(pushError(error)); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true); setMessage("");
    try { await testPush(); setMessage("Testbericht verzonden. Controleer je apparaatmeldingen."); }
    catch (error) { setMessage(pushError(error)); }
    finally { setBusy(false); }
  };

  const explanation = reason || (!configured ? "Pushberichten zijn nog niet ingesteld op de server." : denied
    ? "Meldingen zijn geblokkeerd. Sta meldingen toe bij de site-instellingen van je browser."
    : "Ontvang updates over je planning en ruilverzoeken. Je kiest dit per apparaat.");

  return (
    <section className="px-4 py-3 border-b bg-red-50/60 dark:bg-red-950/20 space-y-2" data-testid="push-settings">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-semibold flex items-center gap-1.5"><BellRing className="w-4 h-4 text-red-600" /> Pushberichten</span>
        <span className="text-xs text-muted-foreground" data-testid="push-state">{enabled ? "Aan" : "Uit"}</span>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="push-help">{explanation}</p>
      {window.self !== window.top && <a href={window.location.href} target="_blank" rel="noopener noreferrer" className="text-xs text-red-700 underline" data-testid="push-open-tab">Open app in apart tabblad</a>}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant={enabled ? "outline" : "default"} disabled={busy || !!reason || !configured || (denied && !enabled)} onClick={toggle} data-testid="push-toggle">
          {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}{enabled ? "Uitschakelen" : "Inschakelen"}
        </Button>
        {enabled && <Button size="sm" variant="outline" disabled={busy} onClick={sendTest} data-testid="push-test">Testbericht</Button>}
      </div>
      {message && <p className="text-xs text-muted-foreground" role="status" aria-live="polite" data-testid="push-feedback">{message}</p>}
    </section>
  );
}
